/**
 * Hook per gestione messaggi della chat corrente
 */

import { useEffect, useCallback, useMemo } from 'react';
import { useWhatsAppChatStore } from '../../stores/whatsappChatStore';
import type { Message, PendingMessage } from '../../types/whatsappChat';

export function useWhatsAppMessages(chatId: string | null) {
  const {
    messages,
    pendingMessages,
    messagesLoading,
    messagesError,
    loadOlderError,
    hasOlderMessages,
    loadMessages,
    loadOlderMessages,
  } = useWhatsAppChatStore();

  // Carica messaggi quando cambia chat
  useEffect(() => {
    if (chatId) {
      loadMessages(chatId);
    }
  }, [chatId, loadMessages]);

  // Combina messaggi reali e pending, ordinati per timestamp (più vecchi prima)
  const allMessages = useMemo(() => {
    const chatPending = pendingMessages.filter((p) => p.chatId === chatId);

    // Trasforma pending in Message-like per visualizzazione
    const pendingAsMessages: Message[] = chatPending.map((p) => ({
      id: p.tempId,
      chatId: p.chatId,
      content: p.content,
      type: p.type,
      direction: 'outbound' as const,
      status: p.status === 'sending' ? 'pending' : 'failed',
      timestamp: p.timestamp,
      mediaUrl: p.mediaPreviewUrl,
      replyToId: p.replyToId,
      replyToBody: p.replyToBody,
    }));

    // Ordina per timestamp ascendente (più vecchi in alto, più recenti in basso)
    const combined = [...messages, ...pendingAsMessages];
    combined.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    return combined;
  }, [messages, pendingMessages, chatId]);

  // Load older messages callback
  const handleLoadMore = useCallback(() => {
    if (hasOlderMessages && !messagesLoading) {
      loadOlderMessages();
    }
  }, [hasOlderMessages, messagesLoading, loadOlderMessages]);

  return {
    messages: allMessages,
    loading: messagesLoading,
    error: messagesError,
    loadOlderError,
    hasOlderMessages,
    loadOlderMessages: handleLoadMore,
  };
}
